import { useState, useEffect, ChangeEvent } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { InputIcon } from '@/components/signup/account-creation/components/input-icon/input-icon.tsx';
import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context.tsx';
import { emailIsValid } from '@/components/signup/utils/email-is-valid.ts';
import { fontSize } from '@/components/signup/utils/font-size.ts';
import { useUserContext } from '@/components/user-context/user-context-provider.tsx';
import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';

export const InputSubmitButton = () => {
	const intl = useIntl();
	const {
		formError,
		hasSubmitted,
		isReadyForInvalid,
		isSubmitting,
		setIsEmailValid,
		setIsReadyForInvalid,
		setTypeAheadEmail,
		typeAheadEmail,
	} = useAccountCreationContext();
	const [isValid, setValid] = useState<boolean>();
	const [email, setEmail] = useState(typeAheadEmail);

	useEffect(() => {
		const isValid = emailIsValid(email);

		// wait to show invalid state if the user is not finished typing
		if (!isValid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}

		setValid(isValid);
		setIsEmailValid(isValid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	const buttonText = intl.formatMessage({
		id: 'signupForm.accountCreation.signupButton',
		defaultMessage: 'Sign up',
	});

	const helperText = intl.formatMessage({
		id: 'signupForm.accountCreation.helperText',
		defaultMessage: 'Using a work email helps find teammates and boost collaboration.',
	});

	const placeholderText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.emailPlaceholder',
		defaultMessage: 'you@company.com',
	});

	const errorMessageText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.invalidEmail',
		defaultMessage: 'Please enter a valid email address.',
	});

	const labelText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.workEmail',
		defaultMessage: 'Work email',
	});

	const productName = useUserContext().product?.key as ProductKeyTypes;

	return (
		<>
			<div css={containerCss}>
				<div css={inputCss}>
					<Field
						aria-required={true}
						isRequired
						label={<span css={labelChildCss}>{labelText}</span>}
						name="email"
					>
						{({ fieldProps }) => (
							<div css={[isValid === true && inputValidCss, isValid === false && inputInvalidCss]}>
								<TextField
									{...fieldProps}
									autoComplete="email"
									aria-describedby="account_creation_email_help_text"
									elemAfterInput={<InputIcon valid={isValid} />}
									onBlur={() => setIsReadyForInvalid(true)}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setEmail(e.target.value);
										setTypeAheadEmail(e.target.value);
										fieldProps.onChange(e);
									}}
									placeholder={placeholderText}
									testId="email_field"
									type="email"
									value={email}
								/>
							</div>
						)}
					</Field>
				</div>
				<div>
					<Button
						type="submit"
						css={buttonCss}
						isDisabled={isSubmitting || (hasSubmitted && !formError)}
						isLoading={isSubmitting || (hasSubmitted && !formError)}
						dataEventProps={{
							'data-product-key': productName,
							'data-event': 'clicked',
							'data-event-component': 'linkButton',
							'data-event-container': 'signup',
							'data-label': 'sign-up',
							'data-label-english': 'Sign up',
						}}
					>
						{buttonText}
					</Button>
				</div>
			</div>
			{isValid === false ? (
				<div css={errorMessageCss} id="account_creation_email_help_text" role="alert">
					{errorMessageText}
				</div>
			) : helperText ? (
				<div css={helpTextCss} id="account_creation_email_help_text">
					{helperText}
				</div>
			) : null}
		</>
	);
};

const containerCss = css({
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'space-between',
	gap: 8,
	marginBlockEnd: 16,
});

const inputCss = css({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderRadius: '6px',
		borderWidth: token('border.width', '1px'),
		paddingBlock: 1,
		['&:focus-within']: {
			borderWidth: token('border.width.focused', '2px'),
			paddingBlock: 0,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		paddingInlineStart: '12px !important',
		fontFamily: 'inherit',
		backgroundColor: `${token('elevation.surface')} !important`,
		fontSize: fontSize.md,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['label']: {
		fontFamily: 'inherit',
		color: 'inherit',
		fontWeight: 500,
		overflow: 'visible',
		width: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
		['[aria-hidden=true]']: {
			display: 'none',
		},
	},
});

const labelChildCss = css({
	display: 'inline-block',
	maxWidth: 'calc(100vw - 48px)',
	width: 400,
});

const buttonCss = css({
	borderRadius: '6px',
	fontSize: '14px',
	height: 45.12, // to match the height of the input field
	whiteSpace: 'nowrap',
	fontFamily: 'inherit',
	minWidth: 'auto',
	paddingBlock: '8px',
});

const supportingText = css({
	margin: '0 auto',
	textAlign: 'center',
	fontSize: fontSize.xs,
	lineHeight: 1.5,
});

const helpTextCss = css({
	...supportingText,
	opacity: 0.8,
});

const errorMessageCss = css({
	...supportingText,
	color: token('color.icon.accent.red'),
});

const inputInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderColor: `${token('color.border.danger')} !important`,
		borderWidth: `${token('border.width.focused', '2px')} !important`,
		paddingBlock: 0,
	},
});

const inputValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderColor: `${wacToken('color.extended.lime600')} !important`,
		borderWidth: `${token('border.width.focused', '2px')} !important`,
		paddingBlock: 0,
	},
});
